import { FC, useCallback } from 'react';
import useAuthenticatedRoute from 'src/common/hooks/useAuthenticatedRoute';
import { useListingsPageContext } from 'src/admin/listing-editor/providers/ListingsPageProvider';
import ListingsHeader from 'src/admin/listing-editor/components/ListingsHeader';
import ListingsContent from 'src/admin/listing-editor/components/ListingsContent';
import ListingCard from 'src/admin/listing-editor/components/ListingCard';
import NoListing from 'src/admin/listing-editor/components/NoListing';
import { useUserContext } from 'src/common/providers/UserProvider';
import NoPayoutBanner from 'src/admin/listing-editor/components/NoPayoutBanner';
import NoPayoutModal from 'src/admin/listing-editor/components/NoPayoutModal';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import ListingPaginationSummary from 'src/common/components/ListingPaginationSummary';
import Pagination from 'src/common/components/Pagination';
import getListingProgress from 'src/admin/listing-editor/utils/getListingProgress';
import SnoozeModal from 'src/admin/common/components/SnoozeModal';
import useListingSnooze from 'src/admin/listing-editor/hooks/useListingSnooze';
import ImpersonationLabel from 'src/admin/common/components/ImpersonationLabel';
import styles from './ListingPage.module.scss';

const ListingsPage: FC = () => {
  useAuthenticatedRoute();
  const {
    ownerListings,
    noListingState,
    pagination,
    getListingPaginatedUrl,
    setPage,
    refresh: refreshPage,
    selectedPublishedListingIds,
    publishedListings,
    isImpersonatedSession,
  } = useListingsPageContext();

  const { validateUserPayoutCurrency } = useCurrencyContext();
  const { user } = useUserContext();

  const {
    closeModal: closeSnoozeModal,
    selectedListingIds: selectedSnoozeListingIds,
    snoozeBulkListings,
    isModalOpen: isSnoozeModalOpen,
    submit: submitSnooze,
  } = useListingSnooze({
    onSubmit: refreshPage,
  });

  const snoozeListings = useCallback(
    (id?: string[]) => {
      const selectedIds = id || selectedPublishedListingIds;
      snoozeBulkListings(selectedIds);
    },
    [selectedPublishedListingIds, snoozeBulkListings],
  );

  const hasValidPayoutCurrency = validateUserPayoutCurrency();
  const hasPayoutMethod = (
    isImpersonatedSession
    || !!(user?.has_payout_method && hasValidPayoutCurrency)
  );
  const listingsExist = ownerListings && ownerListings.length > 0;
  const allListingsProgress = ownerListings.map(
    (listing) => getListingProgress(listing, isImpersonatedSession),
  );

  const {
    close: closePayoutModal,
    isOpen: isPayoutModalOpen,
  } = useOpenCloseToggle(!hasPayoutMethod);

  return (
    <div className={styles.root}>
      <NoPayoutBanner
        hasPayoutMethod={hasPayoutMethod}
        hasValidPayoutCurrency={hasValidPayoutCurrency}
        underline
      />
      <ListingsHeader
        hasPayoutMethod={hasPayoutMethod}
        openSnoozeModal={snoozeListings}
      />
      <SnoozeModal
        listings={publishedListings}
        selectedListingIds={selectedSnoozeListingIds}
        isOpen={isSnoozeModalOpen}
        closeModal={closeSnoozeModal}
        submit={submitSnooze}
      />
      <NoPayoutModal
        closeModal={closePayoutModal}
        open={isPayoutModalOpen}
        hasValidPayoutCurrency={hasValidPayoutCurrency}
      />
      <ListingsContent>
        {listingsExist && (
          <>
            <div className={styles.summary}>
              <ListingPaginationSummary
                rounded={false}
                count={pagination.count}
                limit={pagination.limit}
                page={pagination.page}
              />
            </div>
            <div className={styles.listings}>
              {ownerListings.map((listing, i) => (
                <ListingCard
                  key={listing.id}
                  id={listing.id}
                  active={Boolean(listing.active)}
                  photo={listing.cover_photo_url}
                  headline={listing.headline}
                  shortname={listing.shortname}
                  progress={allListingsProgress[i]}
                  hasPayoutMethod={hasPayoutMethod}
                  snoozeStartDate={listing.snooze_start_date}
                  snoozeEndDate={listing.snooze_end_date}
                  snoozeListing={snoozeListings}
                  underReview={listing.under_review}
                  dateModified={listing.date_modified}
                  datePublished={listing.date_published}
                  isImpersonatedSession={isImpersonatedSession}
                />
              ))}
            </div>
            { pagination.count && pagination.count > pagination.limit && (
              <Pagination
                count={pagination.count}
                limit={pagination.limit}
                page={pagination.page}
                onChange={(page) => {
                  setPage(page);
                }}
                hrefBuilder={(page) => getListingPaginatedUrl({ page })}
                withTotalCount
              />
            )}
          </>
        )}

        {!listingsExist && (
          <NoListing
            state={noListingState}
            hasPayoutMethod={hasPayoutMethod}
          />
        )}
        {isImpersonatedSession && <ImpersonationLabel />}
      </ListingsContent>
    </div>
  );
};

export default ListingsPage;
